import React, { useState, useEffect, useContext } from "react";
import { useFirestore } from "../firebase/FirestoreContext";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import CircularProgress from "@mui/material/CircularProgress"; // Import CircularProgress

const ProductCategories = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editMode, setEditMode] = useState(null);
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [tagInput, setTagInput] = useState({});
  const [subCategoryInput, setSubCategoryInput] = useState({}); // New state for subcategories
  const [newCategorySlug, setNewCategorySlug] = useState("");
  const [editedCategorySlug, setEditedCategorySlug] = useState(""); // Add state for editing slug
  const [uploadingImage, setUploadingImage] = useState(null); // Track uploading image
  const [newCategoryDisplayOnList, setNewCategoryDisplayOnList] = useState(true);
  const [editedCategoryDisplayOnList, setEditedCategoryDisplayOnList] = useState(true);
  const [newCategoryOrder, setNewCategoryOrder] = useState(0);
  const [editedCategoryOrder, setEditedCategoryOrder] = useState(0);

  useEffect(() => {
    fetchCategories();
  }, [db, collectionPath]);

  const fetchCategories = async () => {
    const categoriesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`);
    const querySnapshot = await getDocs(categoriesCollectionRef);
    const categoriesArray = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setCategories(categoriesArray);
  };

  const handleTagInputChange = (categoryId, value) => {
    setTagInput((prev) => ({ ...prev, [categoryId]: value }));
  };

  const handleSubCategoryInputChange = (categoryId, value) => {
    setSubCategoryInput((prev) => ({ ...prev, [categoryId]: value }));
  };

  // upload category image
  const handleUploadImage = async (categoryId, file) => {
    if (!file) return;
    try {
      setUploadingImage(categoryId); // Set uploading state
      const storageRef = ref(getStorage(), `Categories/${categoryId}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const categoryRef = doc(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`, categoryId);
      await updateDoc(categoryRef, { image_url: url });
      setCategories((prev) => prev.map((cat) => (cat.id === categoryId ? { ...cat, image_url: url } : cat)));
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Error uploading image");
    } finally {
      setUploadingImage(null); // Clear uploading state
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }

    try {
      await addDoc(collection(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`), {
        name: newCategoryName,
        categoryName: newCategorySlug,
        subCategories: [],
        display_on_list: newCategoryDisplayOnList,
        order: parseInt(newCategoryOrder, 10) || 0, // Ensure it's a number
      });
      toast.success("Category added successfully");
      setNewCategoryName("");
      setNewCategorySlug("");
      setNewCategoryDisplayOnList(true);
      setNewCategoryOrder(0);
      fetchCategories();
    } catch (error) {
      toast.error("Error adding category");
    }
  };

  const handleEditCategory = async (categoryId) => {
    if (!editedCategoryName.trim()) {
      toast.error("Category title cannot be empty");
      return;
    }

    if (!editedCategorySlug.trim()) {
      toast.error("Category URL cannot be empty");
      return;
    }

    try {
      const categoryRef = doc(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`, categoryId);
      await updateDoc(categoryRef, {
        name: editedCategoryName,
        categoryName: editedCategorySlug, // Update the slug (categoryName)
        display_on_list: editedCategoryDisplayOnList,
        order: parseInt(editedCategoryOrder, 10) || 0, // Ensure it's a number
      });
      toast.success("Category updated successfully");
      setEditMode(null);
      fetchCategories();
    } catch (error) {
      toast.error("Error updating category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      const categoryRef = doc(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`, categoryId);
      await deleteDoc(categoryRef);
      toast.success("Category deleted successfully");
      fetchCategories();
    } catch (error) {
      toast.error("Error deleting category");
    }
  };

  const handleAddSubCategory = async (categoryId) => {
    if (!subCategoryInput[categoryId] || !subCategoryInput[categoryId].trim()) {
      toast.error("SubCategory cannot be empty");
      return;
    }

    try {
      const categoryRef = doc(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`, categoryId);
      const updatedCategory = categories.find((cat) => cat.id === categoryId);
      const updatedSubCategories = [...(updatedCategory.subCategories || []), subCategoryInput[categoryId]];

      await updateDoc(categoryRef, { subCategories: updatedSubCategories });
      toast.success("SubCategory added successfully");
      setSubCategoryInput((prev) => ({ ...prev, [categoryId]: "" }));
      fetchCategories();
    } catch (error) {
      toast.error("Error adding subcategory");
    }
  };

  const handleDeleteSubCategory = async (categoryId, subCategoryToDelete) => {
    try {
      const categoryRef = doc(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`, categoryId);
      const updatedCategory = categories.find((cat) => cat.id === categoryId);
      const updatedSubCategories = updatedCategory.subCategories.filter((subCat) => subCat !== subCategoryToDelete);

      await updateDoc(categoryRef, { subCategories: updatedSubCategories });
      toast.success("SubCategory deleted successfully");
      fetchCategories();
    } catch (error) {
      toast.error("Error deleting subcategory");
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
      <h2>Product Categories</h2>

      {/* Redesigned table with proper structure */}
      <table style={styles.table}>
        <thead>
          <tr style={{ backgroundColor: "rgba(49,57,66,.1)" }}>
            <th style={styles.th}>Category Name</th>
            <th style={styles.th}>Category URL</th>
            <th style={styles.th}>Image</th>
            <th style={styles.th}>Display on List</th>
            <th style={styles.th}>Order</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((category, index) => (
            <React.Fragment key={category.id}>
              <tr style={{ backgroundColor: index % 2 === 0 ? "#fafafa" : "" }}>
                {/* Category Name */}
                <td style={{ ...styles.td, fontWeight: 600 }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {editMode === category.id ? (
                      <input type="text" value={editedCategoryName} onChange={(e) => setEditedCategoryName(e.target.value)} style={styles.input} />
                    ) : (
                      category.name
                    )}
                  </div>
                </td>

                {/* Category Slug */}
                <td style={{ ...styles.td }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {editMode === category.id ? (
                      <input
                        type="text"
                        value={editedCategorySlug}
                        onChange={(e) => setEditedCategorySlug(e.target.value)}
                        style={styles.input}
                        placeholder="Category URL"
                      />
                    ) : (
                      <span style={{ color: "gray", fontSize: "0.9rem" }}>{category.categoryName || "No Category URL"}</span>
                    )}
                  </div>
                </td>

                {/* Category Image */}
                <td style={{ ...styles.td }}>
                  <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                    {uploadingImage === category.id ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 50, height: 50 }}>
                        <CircularProgress size={30} style={{ position: "absolute", zIndex: 100 }} />
                        <img src={category?.image_url} alt={category.name} style={{ width: 50, height: 50, marginRight: 10 }} />
                      </div>
                    ) : category.image_url ? (
                      <img src={category.image_url} alt={category.name} style={{ width: 50, height: 50, marginRight: 10 }} />
                    ) : null}
                    <input
                      type="file"
                      onChange={(e) => handleUploadImage(category.id, e.target.files[0])}
                      accept="image/*"
                      style={styles.fileInput}
                      disabled={uploadingImage === category.id}
                    />
                  </div>
                </td>

                {/* Display on List */}
                <td style={styles.td}>
                  {editMode === category.id ? (
                    <input type="checkbox" checked={editedCategoryDisplayOnList} onChange={(e) => setEditedCategoryDisplayOnList(e.target.checked)} />
                  ) : category.display_on_list ? (
                    "Yes"
                  ) : (
                    "No"
                  )}
                </td>

                {/* Order */}
                <td style={styles.td}>
                  {editMode === category.id ? (
                    <input type="number" value={editedCategoryOrder} onChange={(e) => setEditedCategoryOrder(e.target.value)} style={styles.input} />
                  ) : (
                    category.order || 0
                  )}
                </td>

                {/* Actions */}
                <td style={{ ...styles.td }}>
                  {editMode === category.id ? (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button onClick={() => handleEditCategory(category.id)} style={styles.button}>
                        Save
                      </button>
                      <button onClick={() => setEditMode(null)} style={{ ...styles.button, backgroundColor: "#888" }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button
                        onClick={() => {
                          setEditMode(category.id);
                          setEditedCategoryName(category.name);
                          setEditedCategorySlug(category.categoryName || "");
                          setEditedCategoryDisplayOnList(category.display_on_list);
                          setEditedCategoryOrder(category.order || 0);
                        }}
                        style={styles.button}
                      >
                        Edit
                      </button>
                      <button onClick={() => handleDeleteCategory(category.id)} style={{ ...styles.button, backgroundColor: "red" }}>
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>

              {/* SubCategories Section - in a separate row */}
              <tr style={{ backgroundColor: index % 2 === 0 ? "#fafafa" : "" }}>
                <td colSpan="6" style={{ padding: "0 8px 8px 8px" }}>
                  <div style={{ backgroundColor: "#e6e6e6", padding: "5px 10px", marginBottom: "5px", borderRadius: "4px" }}>Category SubCategories</div>

                  <div style={{ display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center" }}>
                    <input
                      type="text"
                      value={subCategoryInput[category.id] || ""}
                      onChange={(e) => handleSubCategoryInputChange(category.id, e.target.value)}
                      placeholder="Add SubCategory"
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button onClick={() => handleAddSubCategory(category.id)} style={{ ...styles.button, padding: "11px 20px", width: "auto" }}>
                      Add
                    </button>
                  </div>

                  <div style={{ marginTop: "5px" }}>
                    {category.subCategories?.map((subCategory, subIndex) => (
                      <span key={subIndex} style={styles.tag}>
                        {subCategory}{" "}
                        <button onClick={() => handleDeleteSubCategory(category.id, subCategory)} style={styles.deleteTagButton}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </td>
              </tr>

              {/* Add spacing row between categories */}
              <tr style={{ height: "15px" }}>
                <td colSpan="6"></td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* Add new category section */}
      <div
        style={{
          display: "flex",
          marginTop: 20,
          padding: "20px 10px",
          border: "1px solid #ccc",
          borderRadius: 5,
          background: "#f0f0f0",
          flexDirection: "column",
        }}
      >
        <div style={{ marginBottom: 20, fontWeight: "bold" }}>Add New Category</div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "12px", marginBottom: "5px", display: "block" }}>Category Name</label>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="New Category Name"
              style={styles.input}
            />
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "12px", marginBottom: "5px", display: "block" }}>Category URL</label>
            <input
              type="text"
              value={newCategorySlug}
              onChange={(e) => setNewCategorySlug(e.target.value)}
              placeholder="Category URL (e.g. pain-relief)"
              style={styles.input}
            />
          </div>

          <div>
            <label style={{ fontSize: "12px", marginBottom: "5px", display: "block" }}>Display on List</label>
            <input type="checkbox" checked={newCategoryDisplayOnList} onChange={(e) => setNewCategoryDisplayOnList(e.target.checked)} />
          </div>

          <div>
            <label style={{ fontSize: "12px", marginBottom: "5px", display: "block" }}>Order</label>
            <input type="number" value={newCategoryOrder} onChange={(e) => setNewCategoryOrder(e.target.value)} style={styles.input} />
          </div>

          <div style={{ alignSelf: "flex-end" }}>
            <button onClick={handleAddCategory} style={{ ...styles.button, padding: "11px 20px" }}>
              Add Category
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update styles
const styles = {
  table: { borderCollapse: "collapse", width: "100%" },
  th: { padding: "8px", textAlign: "left", borderBottom: "1px solid #ccc", fontSize: 12 },
  td: { padding: "8px" },
  input: { padding: "10px", width: "100%", border: "1px solid #ccc", borderRadius: "4px" },
  button: { padding: "8px 12px", backgroundColor: "#478169", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginLeft: "5px" },
  tag: { marginRight: "5px", padding: "6px 12px", backgroundColor: "#ccc", borderRadius: "25px", display: "inline-block", fontSize: 12 },
  deleteTagButton: { marginLeft: "5px", cursor: "pointer", border: "none", background: "none" },
  addTagButton: { marginLeft: "5px", cursor: "pointer", padding: "5px", backgroundColor: "#478169", color: "white", border: "none", borderRadius: "4px" },
  fileInput: {
    padding: "5px",
    width: "100%",
  },
  // Add any other needed style updates
};

export default ProductCategories;
